/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC, useEffect} from 'react'
import {Routes, Route, BrowserRouter, Navigate} from 'react-router-dom'
import {PrivateRoutes} from './PrivateRoutes'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {Logout, useAuth} from '../modules/auth'
import {App} from '../App'

// Auth/registration live in a SEPARATE static app served by nginx at /auth/* (sandbox-app/Web/AuthApp),
// so this CRA+Blazor bundle no longer ships any auth screen. Logged-out users are hard-redirected there
// (outside this BrowserRouter). AuthInit (App.tsx) holds the splash until token verification settles, so
// by the time this renders the user is genuinely logged out (an invalid token has already been cleared) —
// a valid session lands in the logged-in branch instead and is never bounced.
const RedirectToAuth: FC = () => {
  useEffect(() => {
    window.location.assign('/auth/login')
  }, [])
  return null
}

interface Props {
  baseUrl: string
}

const AppRoutes: FC<Props> = ({ baseUrl }) => {
  const {currentUser} = useAuth()
    return (
      <BrowserRouter basename={baseUrl}>
      <Routes>
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Logout />} />
          {currentUser ? (
            <>
              <Route path='/*' element={<PrivateRoutes />} />
              <Route index element={<Navigate to='/dashboard' />} />
            </>
          ) : (
            <Route path='*' element={<RedirectToAuth />} />
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export {AppRoutes}
